// src/features/userManagement/components/UserTable.jsx

import React, { useState, useRef, useEffect } from 'react';

// Must match backend UserStatus enum: 1=ACTIVE 2=PENDING 3=PENDING_FOR_KYC 4=INACTIVE 5=REJECTED 6=DELETED
const STATUS_LABELS = {
  1: 'Active',
  2: 'Pending',
  3: 'Pending KYC',
  4: 'Inactive',
  5: 'Rejected',
  6: 'Deleted',
};

function StatusBadge({ statusId }) {
  const label = STATUS_LABELS[statusId] ?? String(statusId);
  const colorMap = {
    1: 'bg-green-100 text-green-800',
    2: 'bg-yellow-100 text-yellow-800',
    3: 'bg-yellow-100 text-yellow-800',
    4: 'bg-red-100 text-red-700',
    5: 'bg-red-100 text-red-700',
    6: 'bg-gray-100 text-gray-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorMap[statusId] ?? 'bg-gray-100 text-gray-700'}`}>
      {label}
    </span>
  );
}

function ActionMenu({ user, onEdit, onUpdateStatus, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Actions"
      >
        ⋯
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-40 rounded-lg border border-gray-200 bg-white shadow-lg py-1 text-sm">
          <button
            onClick={() => { setOpen(false); onEdit?.(user); }}
            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Edit
          </button>
          <button
            onClick={() => { setOpen(false); onUpdateStatus?.(user); }}
            className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50"
          >
            Update Status
          </button>
          <button
            onClick={() => { setOpen(false); onDelete?.(user); }}
            className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function UserTable({ users, loading, onEdit, onUpdateStatus, onDelete }) {
  if (loading) {
    return (
      <div className="flex justify-center items-center py-16 text-gray-500">
        Loading users...
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-gray-400 text-4xl mb-3">👤</div>
        <p className="text-gray-600 font-medium">No sub-users yet</p>
        <p className="text-gray-400 text-sm mt-1">Create a user to get started.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {users.map((user) => (
            <tr key={user.client_id} className="hover:bg-gray-50 transition-colors">
              <td className="px-4 py-3">
                <div>
                  <p className="font-medium text-gray-900 text-sm">{user.username}</p>
                  {(user.firstname || user.lastname) && (
                    <p className="text-xs text-gray-400">{[user.firstname, user.lastname].filter(Boolean).join(' ')}</p>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{user.email || '-'}</td>
              <td className="px-4 py-3">
                <span className="text-xs font-mono bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                  {user.user_type}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">{user.role_name || '-'}</td>
              <td className="px-4 py-3">
                <StatusBadge statusId={user.user_status_id} />
              </td>
              <td className="px-4 py-3">
                <ActionMenu user={user} onEdit={onEdit} onUpdateStatus={onUpdateStatus} onDelete={onDelete} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
